import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard-layout";
import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { DashboardContext } from "@/components/dashboard-layout";
import { UserTable } from "@/routes/_dashboard/users/-components/user-table";
import { usersQueryOptions } from "@/server/users";
import { useSearch } from "@tanstack/react-router";
import { SidebarUser } from "./-components/user-sidebar";
import { organisationUsersQueryOptions } from "@/server/users";
import { minuteCounterOverridesQueryOptions } from "@/server/settings";
import { useRightSidebarClose } from "@/hooks/use-right-sidebar-close";
import { useRightSidebarOpen } from "@/hooks/use-right-sidebar-open";
import { CrudEntry } from "@/lib/mma/types.gen";
import {
  checkIfUserMayRead,
  checkIfUserMayWrite,
  checkIfUserMayDelete,
  ContextUser,
  getNextRedirectRoute,
} from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/users")({
  component: () => (
    <DashboardLayout>
      <UsersRouteComponent />
    </DashboardLayout>
  ),
  beforeLoad: ({ context }) => {
    const user = context.user as ContextUser;
    const mayRead = checkIfUserMayRead(user?.profile, user?.accessControl, "USER");

    if (!mayRead) {
      const nextRedirectRoute = getNextRedirectRoute(user);
      throw redirect({ to: nextRedirectRoute });
    }
    return { title: "Users" };
  },
  loader: ({ context }) => ({
    crumb: "Users",
    userProfileId: (context as { user?: { profile: string } }).user?.profile,
    accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
  }),
});

function UsersRouteComponent() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as {
    id?: string;
    search?: string;
    organisation?: number;
    expire?: number;
    tab?: string;
  };
  const context = useContext(DashboardContext);
  const closeRightSidebar = useRightSidebarClose();
  const openRightSidebar = useRightSidebarOpen();
  const [search, setSearch] = useState(searchParams.search);
  const [organisation, setOrganisation] = useState(searchParams.organisation);
  const [expire, setExpire] = useState(searchParams.expire);
  const queryClient = useQueryClient();
  const organisationUsersQueryOptionsMemo = useMemo(
    () => organisationUsersQueryOptions(Number(organisation ?? 0), search, expire),
    [organisation, search, expire]
  );
  const organisationUsersQuery = useQuery(organisationUsersQueryOptionsMemo);
  const organisationUsers = organisationUsersQuery.data;
  const usersQueryOptionsMemo = useMemo(() => usersQueryOptions(search, expire), [search, expire]);
  const usersQuery = useQuery(usersQueryOptionsMemo);
  const users = usersQuery.data;
  const minuteCounterOverridesQueryOptionsMemo = useMemo(() => minuteCounterOverridesQueryOptions, []);
  const minuteCounterOverridesQuery = useQuery(minuteCounterOverridesQueryOptionsMemo);
  const minuteCounterOverrides = minuteCounterOverridesQuery.data?.minuteCounterOverrides;
  const { userProfileId, accessControl } = Route.useLoaderData();
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "USER");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "USER");
  const [isContextReady, setIsContextReady] = useState(false);

  if (!context) {
    console.error("DashboardContext is null. Ensure DashboardLayout wraps this component.");
    return <div>Error: DashboardContext is not available.</div>;
  }

  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  useEffect(() => {
    setSearch(searchParams.search);
    setOrganisation(searchParams.organisation);
    const expireDate = new Date(searchParams.expire ?? "");
    if (isNaN(expireDate.getTime())) {
      setExpire(undefined);
    } else {
      setExpire(parseInt(expireDate.getTime().toFixed(0)));
    }
  }, [searchParams]);

  useEffect(() => {
    closeRightSidebar([
      "id",
      "tab",
      "transactionsPage",
      "transactionsPerPage",
      "transactionsSortBy",
      "transactionsSortDir",
    ]);
  }, [searchParams.organisation, closeRightSidebar]);

  const closeAfterSave = useCallback(() => {
    closeRightSidebar([
      "id",
      "tab",
      "transactionsPage",
      "transactionsPerPage",
      "transactionsSortBy",
      "transactionsSortDir",
    ]);
  }, [closeRightSidebar]);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "users";
      },
    });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === "organisationUsers" && key[1] === Number(organisation ?? 0);
      },
    });
  }, [queryClient, organisation]);

  const handleUserAction = useCallback(() => {
    invalidateQueries();
    closeAfterSave();
  }, [invalidateQueries, closeAfterSave]);

  const paramKeys = useMemo(
    () =>
      ["id", "tab", "transactionsPage", "transactionsPerPage", "transactionsSortBy", "transactionsSortDir"] as const,
    []
  );

  // Build sidebar component based on search params
  const sidebarComponent = useMemo(() => {
    if (!searchParams.id) return null;

    if (searchParams.id === "new") {
      return (
        <SidebarUser
          userId={null}
          organisationId={null}
          onUserCreated={handleUserAction}
          minuteCounterOverrides={minuteCounterOverrides ?? null}
          closeAfterSave={closeAfterSave}
          mayWrite={mayWrite}
        />
      );
    }

    if (users?.length) {
      const user = users.find((u) => u.id === searchParams.id);
      if (user) {
        return (
          <SidebarUser
            userId={user.id}
            organisationId={user.organisation}
            onUserUpdated={handleUserAction}
            onUserDeleted={handleUserAction}
            closeAfterSave={closeAfterSave}
            minuteCounterOverrides={minuteCounterOverrides ?? null}
          />
        );
      }
    }

    return null;
  }, [searchParams.id, users, handleUserAction, closeAfterSave, mayWrite]);

  useEffect(() => {
    if (!context?.setRightSidebar) {
      return;
    }

    const hasPermission = searchParams.id !== "new" || mayWrite;

    openRightSidebar({
      component: sidebarComponent,
      paramKeys: paramKeys as any,
      shouldClose: false,
      permissionCheck: !hasPermission,
      onPermissionDenied: () => closeAfterSave(),
    });
  }, [sidebarComponent, mayWrite, searchParams.id, openRightSidebar, closeAfterSave, paramKeys]);

  const handleAddUser = () => {
    navigate({
      to: "/users",
      replace: true,
      search: (prev) => ({ ...prev, id: "new" }),
    });
  };

  return (
    <UserTable
      users={organisation && organisation > 0 ? organisationUsers : users}
      handleAddUser={handleAddUser}
      isLoading={usersQuery.isLoading}
      mayWrite={mayWrite}
      mayDelete={mayDelete}
    />
  );
}
